import { useEffect, useRef } from 'react';

/**
 * Custom hook to reset filters and view mode on full page reload.
 * Only runs once on mount — the navigation type never changes for the page lifetime,
 * so re-running on dependency changes would reset filters after every state change.
 * 
 * IMPORTANT: This should only reset on actual browser reloads (F5, Ctrl+R), not on
 * React Router navigation (including back button navigation).
 * 
 * @param {Function} resetFilters - Function to reset filters
 * @param {Function} setViewMode - Function to set view mode
 */
function useReloadReset(resetFilters, setViewMode) {
  const hasRun = useRef(false);

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    // Check if this is truly a page reload and not a React Router navigation
    // When navigating via React Router (including back button), history.state.idx exists
    // When doing a fresh reload, history.state is null or doesn't have idx
    const isReactRouterNavigation = window.history?.state?.idx !== undefined && window.history?.state?.idx !== null;
    
    // If this is React Router navigation (back button, etc.), don't reset
    if (isReactRouterNavigation) {
      return;
    }

    try {
      // Modern API - only reset if it's an actual reload
      const navigation = performance.getEntriesByType('navigation')[0];
      if (navigation && navigation.type === 'reload') {
        resetFilters();
        setViewMode('grid');
        return;
      }
    } catch {
      // Fallback to deprecated but widely supported API
      if (performance.navigation && performance.navigation.type === 1) {
        // Type 1 = TYPE_RELOAD
        resetFilters();
        setViewMode('grid');
      }
    }
  }, [resetFilters, setViewMode]);
}

export default useReloadReset;
